import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { submitMortgageClauseTemplate } from 'services/apis/conditions'
import { svgLoading } from 'stories/assets'
import { Button } from 'stories/components'
import { InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import type { IMortgageClauseTemplate } from './types'

const mortgageClauseInputs = () => {
  return {
    title: {
      inputType: 'text',
      title: 'Title',
      value: '',
      error: '',
      required: true,
      key: 'title',
    },
    content: {
      inputType: 'textarea',
      title: 'Content',
      value: '',
      error: '',
      key: 'content',
      required: true,
      span: 'full',
      row: 5,
    },
  }
}

export function MortgageClauseTemplateDetails(props: {
  defaults: IMortgageClauseTemplate | null
  lastUpdatedAt: number
  type: string
  onBack: Function
  onComplete: Function
}) {
  const [isLoading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, any>>({})
  const { defaults = null, lastUpdatedAt, type } = props

  useEffect(() => {
    if (!defaults) {
      setInputs(mortgageClauseInputs())
      return
    }
    const newStats = cloneDeep(mortgageClauseInputs()) as Record<string, any>
    for (const key in newStats) {
      newStats[key].value = (defaults as any)[key]
    }
    setInputs(newStats)
  }, [defaults, lastUpdatedAt])

  const onBack = () => {
    props.onBack()
  }

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''
    setInputs(newInputs)
  }

  const onSubmit = () => {
    let hasError = false
    const newStats = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in newStats) {
      const { value, disabled = false } = newStats[key]
      let error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) hasError = true
      if (!disabled && value !== undefined) data[key] = value
    }

    if (hasError) {
      setInputs(newStats)
      return
    }

    data.type = type

    setLoading(true)
    const id = defaults ? defaults.id : 0
    submitMortgageClauseTemplate(id, data)
      .then(() => {
        if (id === 0) toast('New template is added.', { type: 'success' })
        else toast('Template is updated.', { type: 'success' })
        props.onComplete()
        onBack()
      })
      .catch(() => setLoading(false))
  }

  return (
    <div className="mortgage-clause-template-details-container">
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Mortgage Clause Template - {!defaults ? 'Add' : 'Update'}
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <Button link onClick={onBack}>
        <div className="flex text-shade-blue items-center">
          <ArrowLeftIcon className="w-4 h-4 mr-1" /> <p>Return to Templates</p>
        </div>
      </Button>

      <div className="grid gap-4 md:grid-cols-2 grid-cols-1 mb-3 mt-4">
        {Object.keys(inputs).map((key, index) => {
          let input = inputs[key]
          return (
            <div className={`input col-span-${input.span || 1}`} key={index}>
              <RenderInput input={input} Key={key} onChange={onChange} />
            </div>
          )
        })}
      </div>
      <div className="block text-center">
        <Button onClick={onSubmit} className="px-10" loading={isLoading}>
          <>{defaults ? 'Update' : 'Add'} Template</>
        </Button>
      </div>
    </div>
  )
}
